import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "~/components/buttons/Button";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import RadioGroup from "~/components/formElements/RadioGroup";
import SubmitButton from "~/components/formElements/SubmitButton";
import Textarea from "~/components/formElements/Textarea";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import { updateBillingDetails } from "~/modules/project/api/project/projectApiDispatchers.ts";
import type { Project } from "~/modules/project/api/project/projectTypes.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type FormData = {
	billOrdersSeparately: boolean;
	billingInstructions: string;
}

type Props = {
	onCancel: () => void;
	onSuccess: (billOrdersSeparatelyChanged: boolean) => void;
	projectData: Project;
};

const UpdateProjectBillingInformationForm = ({
	onCancel,
	onSuccess,
	projectData,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");

	const schema = useMemo(() => {
		return yup.object({
			billOrdersSeparately: yup.boolean().required(),
			billingInstructions: yup.string().default(""),
		});
	}, []);
	const defaultValues = useMemo(() => {
		return {
			billOrdersSeparately: projectData.billOrdersSeparately,
			billingInstructions: projectData.billingInstructions ?? "",
		};
	}, [projectData.billOrdersSeparately, projectData.billingInstructions]);

	const {
		handleSubmit,
		control,
		formState: { isDirty, isSubmitted, isValid },
	} = useForm<FormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<FormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
	});

	const onSubmit = useCallback(async (data: FormData) => {
		try {
			setIsBusy(true);
			await updateBillingDetails({
				projectId: projectData.id,
				updateData: data,
			});
			onSuccess(data.billOrdersSeparately !== projectData.billOrdersSeparately);
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
		}
	}, [onSuccess, projectData.billOrdersSeparately, projectData.id]);

	const radioGroupOptions = useMemo(() => {
		return [{
			label: "Ja",
			value: false,
		}, {
			label: "Nein",
			value: true,
		}];

	}, []);

	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Abrechnungsinformationen bearbeiten</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Update failed">{serverErrorMessage}</SidebarErrorOverlay>}
				<FormSectionGroup>
					<FormSection title="Sammelrechnung für alle Phasen">
						<RadioGroup control={control}
									name="billOrdersSeparately"
									options={radioGroupOptions}
						/>
					</FormSection>
					<FormSection title="Hinweise zur Abrechnung">
						<Textarea name="billingInstructions"
								  control={control} />
					</FormSection>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					Speichern
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					Abbrechen
				</Button>
			</SidebarFooter>
		</form>);
};

export default UpdateProjectBillingInformationForm;
